import { ReactNode, useEffect, useId, useState } from "react";
import { Box, Button, Skeleton, SxProps, Typography } from "@mui/material";
import { ProctoringSessionSummary } from "@verity/api-client/types";
import { useFakeProgress } from "@verity/components";
import { CoreButton, CoreIconButton, CoreProgressBar, CoreTextField } from "@verity/components/core";
import i18n from "../../../../i18n";
import OverallSummaryIcon from "../../../../images/reports/overallSummaryIcon.svg?react";
import SevereIcon from "../../../../images/reports/severeIcon.svg?react";
import MajorIcon from "../../../../images/reports/majorIcon.svg?react";
import MinorIcon from "../../../../images/reports/minorIcon.svg?react";
import CloseXIcon from "../../../../images/reports/closeX.svg?react";
import AiSummaryIcon from "../../../../images/reports/aiSummaryIcon.svg?react";
import circleLoadingGif from "../../../../images/reports/circleLoadingGif.gif";

function SummaryCard({
	title,
	body,
	icon,
	titleColor,
	backgroundColor,
}: {
	title: string;
	body: string;
	icon: ReactNode;
	titleColor: string;
	backgroundColor: string;
}) {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: "5px",
				width: "calc(100% - 10px)",
				height: "fit-content",
				minHeight: "fit-content",
				maxHeight: "fit-content",
				padding: "5px 12.5px 10px 12.5px",
				marginRight: "10px",
				backgroundColor: backgroundColor,
				borderRadius: "8px",
				boxSizing: "border-box",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "start",
					alignItems: "center",
					gap: "5px",
					width: "100%",
					svg: {
						width: "24px",
						height: "24px",
					},
				}}
			>
				{icon}
				<Typography fontWeight="600" sx={{ color: titleColor }}>
					{title}
				</Typography>
			</Box>
			<Typography>{body}</Typography>
		</Box>
	);
}

function FakeProgressBar() {
	const progress = useFakeProgress(false);
	return (
		<CoreProgressBar
			variant="determinate"
			value={progress * 100}
			height={8}
			color={"#7C3AED"}
			backgroundColor={"#E9D5FF"}
			width={"426px"}
			sx={{ maxWidth: "100%" }}
		/>
	);
}

function SummaryContainer({ children, aiGenerating = false }: { children?: ReactNode; aiGenerating?: boolean }) {
	const [borderHighlighted, setBorderHighlighted] = useState(aiGenerating);

	useEffect(() => {
		if (!aiGenerating && borderHighlighted) {
			const timeout = setTimeout(() => {
				setBorderHighlighted(false);
			}, 4000);
			return () => clearTimeout(timeout);
		}
	}, [aiGenerating]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "stretch",
				gap: "20px",
				height: "475px",
				maxHeight: "100%",
				minHeight: 0,
				border: borderHighlighted ? "4px solid transparent" : "1px solid #E4E4E4",
				background: borderHighlighted
					? `linear-gradient(#FFF, #FFF) padding-box, linear-gradient(96deg, #6B55FE -24.53%, #9350C7 24.99%, #ED7EF8 75.81%, #FFEC41 110.99%) border-box`
					: "#FFF",
				borderRadius: "10px",
				boxShadow: "0px 4px 6px -4px #0000001A, 0px 10px 15px -3px #0000001A",
				overflowY: "auto",
			}}
		>
			{aiGenerating ? (
				<Box
					role="status"
					aria-live="polite"
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						gap: "40px",
						maxWidth: "100%",
					}}
				>
					<img src={circleLoadingGif} style={{ width: "180px", height: "135px" }} role="presentation" alt="" />
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: "5px",
							maxWidth: "100%",
							padding: "0px 10px",
							textAlign: "center",
						}}
					>
						<Typography style={{ color: "#6B6B6B" }}>
							{i18n.t(
								"result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.analyzingProctoringData",
							)}
						</Typography>
						<FakeProgressBar />
					</Box>
				</Box>
			) : (
				<Box
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						justifyContent: "start",
						alignItems: "stretch",
						gap: "20px",
						height: "fit-content",
						padding: "5px 10px 20px 20px",
						boxSizing: "border-box",
					}}
				>
					{children}
				</Box>
			)}
		</Box>
	);
}

function MainSummarySection({
	onClose,
	title,
	children,
	sx,
	headerId,
}: {
	onClose: () => void;
	title: ReactNode;
	children: ReactNode;
	sx?: SxProps;
	headerId?: string;
}) {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "stretch",
				gap: "5px",
				...sx,
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					width: "100%",
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "start",
						alignItems: "center",
						gap: "5px",
						paddingTop: "10px",
					}}
				>
					<OverallSummaryIcon width="18px" height="18px" />
					<Typography id={headerId} component="h2" fontWeight="600">
						{title}
					</Typography>
				</Box>
				<CoreIconButton
					variant="plain"
					aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.close")}
					onClick={onClose}
					color="#6B6B6B"
				>
					<CloseXIcon />
				</CoreIconButton>
			</Box>
			{children}
		</Box>
	);
}

export function AiSummary({ onClose, data }: { onClose: () => void; data: ProctoringSessionSummary | undefined }) {
	const summaryDataPopulated = !!data && Object.keys(data).length > 0;
	return (
		<SummaryContainer aiGenerating={!summaryDataPopulated}>
			{summaryDataPopulated ? (
				<>
					{data.summary?.summary && (
						<MainSummarySection
							onClose={onClose}
							title={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.overallSummary")}
						>
							<Typography sx={{ marginRight: "10px" }}>{data.summary?.summary}</Typography>
						</MainSummarySection>
					)}
					{data.summary?.severe && (
						<SummaryCard
							title={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.severe")}
							body={data.summary.severe}
							icon={<SevereIcon />}
							titleColor="#C84A2B"
							backgroundColor="#FEF2F2"
						/>
					)}
					{data.summary?.major && (
						<SummaryCard
							title={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.major")}
							body={data.summary.major}
							icon={<MajorIcon />}
							titleColor="#B57025"
							backgroundColor="#B570250D"
						/>
					)}
					{data.summary?.minor && (
						<SummaryCard
							title={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.minor")}
							body={data.summary.minor}
							icon={<MinorIcon />}
							titleColor="#556C32"
							backgroundColor="#556C320D"
						/>
					)}
				</>
			) : undefined}
		</SummaryContainer>
	);
}

function SummaryEditor({
	getDefaultSummary,
	onClose,
	onSave,
	headerId,
}: {
	getDefaultSummary: () => Promise<string>;
	onClose: () => void;
	onSave: (summary: string) => Promise<void>;
	headerId: string;
}) {
	const [text, setText] = useState("");
	const [loading, setLoading] = useState(true);
	useEffect(() => {
		getDefaultSummary()
			.then(setText)
			.finally(() => setLoading(false));
	}, []);

	const [saving, setSaving] = useState(false);

	return (
		<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
			{loading ? (
				<Skeleton sx={{ flex: 1, transform: "none" }} />
			) : (
				<CoreTextField
					multiline
					value={text}
					onChange={(e) => setText(e.target.value)}
					onKeyDown={(e) => e.stopPropagation()}
					placeholder={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.summaryFieldPlaceholder")}
					sx={{ flex: 1 }}
					InputProps={{ sx: { flex: 1, alignItems: "start" } }}
					inputProps={{ "aria-labelledby": headerId }}
				/>
			)}
			<Box sx={{ display: "flex", justifyContent: "end", gap: "10px" }}>
				<CoreButton color="secondary" onClick={onClose}>
					{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.cancel")}
				</CoreButton>
				<CoreButton
					onClick={async () => {
						setSaving(true);
						try {
							await onSave(text);
							onClose();
						} finally {
							setSaving(false);
						}
					}}
					disabled={loading || !text}
					loading={saving}
				>
					{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.save")}
				</CoreButton>
			</Box>
		</Box>
	);
}

export function LiveProctorSummary({
	onClose,
	data,
	getDefaultSummary,
	onSave,
}: {
	onClose: () => void;
	data: ProctoringSessionSummary | undefined;
	getDefaultSummary: () => Promise<string>;
	onSave: (summary: string) => Promise<void>;
}) {
	const headerId = useId();
	return (
		<SummaryContainer>
			<MainSummarySection
				onClose={onClose}
				title={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.liveProctorSummary")}
				sx={{ flex: 1 }}
				headerId={headerId}
			>
				{data?.source === "liveProctor" && data.summary ? (
					<Typography sx={{ marginRight: "10px" }}> {data.summary?.summary}</Typography>
				) : (
					<SummaryEditor getDefaultSummary={getDefaultSummary} onClose={onClose} onSave={onSave} headerId={headerId} />
				)}
			</MainSummarySection>
		</SummaryContainer>
	);
}

export function AiSummaryButton({ summaryExists, onClick }: { summaryExists: boolean; onClick: () => void }) {
	return (
		<Button
			onClick={onClick}
			sx={{
				width: "190px",
				height: "38px",
				gap: "5px",
				paddingRight: "4.5px",
				paddingLeft: "4.5px",
				color: "#FFFFFF",
				borderRadius: "6px",
				background: "linear-gradient(114.71deg, rgba(91, 74, 206, 0.8) 9.52%, rgba(136, 43, 208, 0.8) 133.56%)",
				fontSize: "15px",
				fontWeight: "500",
				boxSizing: "border-box",
				textTransform: "none",
				"&:hover, &:active, &:focus-visible": {
					background: "linear-gradient(272.07deg, rgba(91, 74, 206, 0.8) -16.93%, rgba(136, 43, 208, 0.8) 145.81%)",
				},
				"&:focus-visible": {
					outline: "2px solid #0b6f85",
					outlineOffset: "2px",
				},
			}}
		>
			<AiSummaryIcon />
			{summaryExists
				? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.viewSummary")
				: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.generateAiSummary")}
		</Button>
	);
}
